import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQueryWithAuth } from "~/ui-rtk/utils/base-query"
import { emptySplitApi } from "./emptyApi"

export interface GenerateAdsetsRequest {
  campaignType: string
  optimizationGoal: string
  targetingGeography: string
  targetingGender: string
  placements: string
}

export interface GeneratedAdset {
  title: string
  description: string
  exampleHandles?: string[]
  creativeIds?: string[]
}

export interface GeneratedStructure {
  name: string
  description: string
  adsets: GeneratedAdset[]
}

export interface GenerateAdsetsResponse {
  structures: GeneratedStructure[]
}

export interface StartGenerateResponse {
  operationId: string
}

export interface GenerateStatusResponse {
  status: "processing" | "completed" | "failed"
  result?: GenerateAdsetsResponse
  error?: string
  createdAt: number
}

export interface SelectedCreativeMetrics {
  brandValue: number
  spend?: number
  purchaseValue?: number
  comments?: number
  likes?: number
  shares?: number
  saves?: number
  reach?: number
}

export interface SelectedCreativePayload {
  id: string
  type: "ad" | "organic"
  title: string
  mediaChannel: string
  thumbnail?: string
  metrics: SelectedCreativeMetrics
}

export interface GenerateCreativeAdsetsRequest {
  creatives: SelectedCreativePayload[]
  campaignType: string
  optimizationGoal: string
}

export type GenerateCreativeAdsetsResponse = GenerateAdsetsResponse

export interface LaunchCreativePayload {
  id: string
  type: "ad" | "organic"
  title: string
  mediaChannel: string
}

export interface CreativeOverridesPayload {
  destinationUrl?: string
  headline?: string
  primaryText?: string
  callToAction?: string
}

export interface LaunchCampaignRequest {
  campaignTitle: string
  campaignType: string
  optimizationGoal: string
  attributionModel: string
  attributionWindow: string
  adsetStrategy: string
  targetingGeography: string
  targetingGender: string
  placements: string
  platforms: string[]
  budgetOptimization: string
  biddingStrategy: string
  budgetAmount: number
  ageMin: number
  ageMax: number
  pixelId?: string
  adsets: { title: string; description?: string; creativeIds?: string[] }[]
  selectedCreatives?: LaunchCreativePayload[]
  creativeOverrides?: CreativeOverridesPayload
}

export interface MetaAdAccountOption {
  id: string
  name: string
}

export interface PixelOption {
  id: string
  name: string
}

export interface PixelsResponse {
  pixels: PixelOption[]
  recommendedPixelId: string
  recommendedReason: string
}

export interface AdCreationResult {
  adsetId: string
  creativeId: string
  creativeTitle: string
  success: boolean
  adId?: string
  error?: string
}

export interface LaunchCampaignResponse {
  campaignId: string
  adsetIds: string[]
  campaignTitle: string
  adResults?: {
    total: number
    successful: number
    failed: number
    details: AdCreationResult[]
  }
}

export const campaignBlasterApi = createApi({
  reducerPath: "campaignBlasterApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["MetaAdAccount"],
  endpoints: builder => ({
    generateAdsets: builder.mutation<
      StartGenerateResponse,
      GenerateAdsetsRequest
    >({
      query: (body: GenerateAdsetsRequest) => ({
        url: "/campaign-blaster/generate-adsets",
        method: "POST",
        body,
      }),
    }),
    generateCreativeAdsets: builder.mutation<
      StartGenerateResponse,
      GenerateCreativeAdsetsRequest
    >({
      query: (body: GenerateCreativeAdsetsRequest) => ({
        url: "/campaign-blaster/generate-creative-adsets",
        method: "POST",
        body,
      }),
    }),
    generateStatus: builder.query<GenerateStatusResponse, string>({
      query: (operationId: string) => ({
        url: `/campaign-blaster/generate-status/${operationId}`,
      }),
    }),
    launchCampaign: builder.mutation<
      LaunchCampaignResponse,
      LaunchCampaignRequest
    >({
      query: (body: LaunchCampaignRequest) => ({
        url: "/campaign-blaster/launch",
        method: "POST",
        body,
      }),
    }),
    getPixels: builder.query<
      PixelsResponse,
      { optimizationGoal: string; campaignType: string }
    >({
      query: ({ optimizationGoal, campaignType }) => ({
        url: "/campaign-blaster/pixels",
        params: { optimizationGoal, campaignType },
      }),
    }),
    getAdAccounts: builder.query<MetaAdAccountOption[], void>({
      query: () => ({ url: "/campaign-blaster/ad-accounts" }),
    }),
    getCurrentAdAccount: builder.query<{ adAccountId: string | null }, void>({
      query: () => ({ url: "/campaign-blaster/ad-account" }),
      providesTags: ["MetaAdAccount"],
    }),
    setAdAccount: builder.mutation<
      { adAccountId: string },
      { adAccountId: string }
    >({
      query: body => ({
        url: "/campaign-blaster/ad-account",
        method: "POST",
        body,
      }),
      invalidatesTags: ["MetaAdAccount"],
      // Meta access (APP-566) lives in the other slice and reads the binding:
      // the card's connector ad account (the Assign pre-fill), Distribute's
      // suggestions and the inventory's `connector_binding` holders.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          return
        }
        dispatch(
          emptySplitApi.util.invalidateTags([
            "MetaAccess",
            "MetaAccountAssignment",
            "MetaAccountUser",
          ]),
        )
      },
    }),
  }),
})

export const {
  useGenerateAdsetsMutation,
  useGenerateCreativeAdsetsMutation,
  useLazyGenerateStatusQuery,
  useLaunchCampaignMutation,
  useLazyGetPixelsQuery,
  useGetAdAccountsQuery,
  useGetCurrentAdAccountQuery,
  useSetAdAccountMutation,
} = campaignBlasterApi
